import { TaskState, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import { ExecutionState } from "../../types";
import { ProxyAgent } from "../../utils/proxy";
import type { SteelAction } from "./index";

const scanRetryMaxCount = 60; // 二维码扫描重试次数
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000; // 最长等待时间

export const rpaServer: SteelAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    let executionState: ExecutionState | undefined;

    let proxyUrl: string | undefined;
    if (params.proxyLoc) {
        task.logger?.info(`==> 开始获取代理信息：${params}`);
        const ProxyAgentResult = await ProxyAgent({ logger: task.logger }, params.proxyLoc, params.accountId);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = ProxyAgentResult ? `http://${ProxyAgentResult.ip}:${ProxyAgentResult.port}` : undefined;
    }
    const page = await task.createPage({
        url: "https://creator.douyin.com/",
        proxyUrl,
        userAgent: params.userAgent,
        ...(params.viewport ? { viewport: params.viewport } : {}),
    });

    await page.route("**/*.mp4", (route) => route.abort()); // 拦截部分不必要的请求，提升加载速度
    await page.route("**/*.png", (route) => route.abort()); // 拦截部分不必要的请求，提升加载速度
    await page.route("**/*.ttf", (route) => route.abort()); // 拦截部分不必要的请求，提升加载速度
    await page.addInitScript(() => {
        window.requestAnimationFrame = () => 0;
    });

    task.logger.info("页面创建成功，开始Bypass页面事件屏蔽...");
    await page.waitForResponse((response) => response.url().includes("/272.6591d0af.js") && response.status() === 200, {
        timeout: 15 * 1000,
    });
    await page.waitForLoadState("networkidle");
    await page.goto("about:blank");
    await page.goBack({ waitUntil: "domcontentloaded" }); // 用于去除页面的无法进行操作的脚本监听，不可删除
    task.logger.info("已返回登录页面，页面事件屏蔽Bypass完成...");

    await page.locator(".douyin-creator-master-icon-default.douyin-creator-master-icon-user_circle").click();

    task.steelBrowser?.on("disconnected", async () => {
        const browserContext = task.steelBrowserContext;
        const browser = task.steelBrowser;
        executionState = ExecutionState.BROWSER_CLOSED;
        await page.close();

        if (browserContext) {
            await browserContext.close();
        }
        if (browser) {
            await browser.close();
        }
    });

    // 拦截不必要的资源请求
    // await page.route(/\/welcome.*\.(png|mp4)$/i, (route) => route.abort());

    await updateTaskState?.({
        state: TaskState.WAIT_SCAN,
        connectAddress: await task.steelConnector?.getLive(task.sessionId || ""),
        sessionId: task.sessionId,
    });

    const userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            const timer = setTimeout(async () => {
                cleanup();
                executionState = ExecutionState.TIMEOUT;
                await updateTaskState?.({
                    state: TaskState.TIMEOUT,
                    error: "等待扫码登录超时",
                });
                resolve();
            }, waitQrcodeResultMaxTime);

            const cleanup = () => {
                if (finished) return; // 防止多次调用
                finished = true;
                clearTimeout(timer);
                page.off("response", handleWebCommon);
                page.off("response", handleCheckQR);
                task.steelBrowser?.off("disconnected", handleBrowserClosed);
            };

            const handleBrowserClosed = () => {
                cleanup();
                executionState = ExecutionState.BROWSER_CLOSED;
                resolve();
            };

            const handleWebCommon = async (resp: Response) => {
                try {
                    const url = resp.url();

                    // 登录成功后的第一个请求
                    if (url.includes("/web/common")) {
                        cleanup();

                        const headers = resp.headers();
                        const xmst = headers["x-ms-token"];
                        const date = headers["x-tt-timestamp"];

                        if (xmst && date) {
                            const mstExpirDate = new Date(Number(date) * 1000 + 7 * 24 * 60 * 60 * 1000);
                            const msToken = {
                                name: "msToken",
                                value: xmst,
                                domain: ".bytedance.com",
                                expires: mstExpirDate.getTime() / 1000,
                                httpOnly: true,
                                path: "/",
                                sameSite: "None" as const,
                                secure: true,
                            };
                            await page.context().addCookies([msToken]);
                            await updateTaskState?.({
                                state: TaskState.CONFIRMED,
                            });
                        }

                        executionState = ExecutionState.SUCCESS;
                        resolve();
                    } else if (url.includes("aweme/v1/creator/user/info")) {
                        // 优先获取用户信息
                        try {
                            const jsonUserInfo: { user_profile: { avatar_url: string; nick_name: string } } =
                                await resp.json();

                            userInfo.name = jsonUserInfo.user_profile.nick_name ?? "";
                            userInfo.avatar = jsonUserInfo.user_profile.avatar_url ?? "";
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", { url, error: e });
                        }
                    } else if (url.includes("/aweme/v1/creator/pc/user/info/")) {
                        // 获取用户信息
                        try {
                            const jsonUserInfo: { uid: string } = await resp.json();
                            userInfo.uniqueId = jsonUserInfo.uid ?? "";
                            task.debug && task.logger.info("用户uniqueId 获取成功", userInfo);
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", { url, error: e });
                        }
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 0=用户未扫码，5=用户扫码已超时，2=用户已扫码未确认，3=扫码成功
            const handleCheckQR = async (response: Response) => {
                try {
                    const url = response.url();
                    if (!url.includes("/check_qrconnect")) return;

                    let jsonResponse: {
                        data: {
                            status?: "new" | "scanned" | "expired" | "confirmed";
                            error_code: number;
                            user_data?: {
                                user_id_str: string;
                            };
                        };
                    };
                    try {
                        jsonResponse = await response.json();
                    } catch (e) {
                        // ⚠️ 这里一定要直接 return，不要再 warn 成 error
                        task.logger.warn("Response body unavailable (likely navigation happened)", {
                            url,
                        });
                        return;
                    }

                    const status = jsonResponse?.data?.status;

                    if (status === "scanned") {
                        await updateTaskState?.({
                            state: TaskState.SCANNED,
                        });
                        page.on("response", handleWebCommon);
                    }

                    if (status === "confirmed") {
                        userInfo.uniqueId = jsonResponse.data?.user_data?.user_id_str || "";

                        page.off("response", handleCheckQR);
                        // page.on("response", handleWebCommon);
                    }
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            };

            // 在页面加载之前就注册监听，防止丢失请求
            task.steelBrowser?.on("disconnected", handleBrowserClosed);
            page.on("response", handleCheckQR);
        });
    } finally {
        await page.close();
    }

    if (executionState === undefined) return { code: 500, message: "未知错误", data: {} };

    switch (executionState) {
        case ExecutionState.BROWSER_CLOSED: {
            const msg = "浏览器已关闭，任务中断!";
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: "浏览器已关闭",
            });
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.TIMEOUT: {
            const msg = "等待用户扫码登录超时!";
            await updateTaskState?.({
                state: TaskState.TIMEOUT,
                error: msg,
            });
            task.logger.info(msg);
            return { code: 414, message: msg, data: {} };
        }

        case ExecutionState.SUCCESS: {
            // 提取 localStorage 中的 security-sdk 数据
            // let securityData: Record<string, string> = {};
            // try {
            //     securityData = await page.evaluate(() => {
            //         return {
            //             "security-sdk/s_sdk_pri_key": localStorage.getItem("security-sdk/s_sdk_pri_key") || "",
            //             "security-sdk/s_sdk_pub_key": localStorage.getItem("security-sdk/s_sdk_pub_key") || "",
            //             "security-sdk/s_sdk_sign_data_key":
            //                 localStorage.getItem("security-sdk/s_sdk_sign_data_key/web_protect") || "",
            //         };
            //     });

            //     task.logger.info("Security SDK 数据提取成功:", {
            //         "security-sdk/s_sdk_pri_key": !!securityData["security-sdk/s_sdk_pri_key"],
            //         "security-sdk/s_sdk_pub_key": !!securityData["security-sdk/s_sdk_pub_key"],
            //         "security-sdk/s_sdk_sign_data_key": !!securityData["security-sdk/s_sdk_sign_data_key"],
            //     });
            // } catch (error) {
            //     task.logger.warn("提取 Security SDK 数据失败:", {
            //         error: error instanceof Error ? error.message : String(error),
            //     });
            // }
            let securityData: Record<string, string> = {};
            let retryCount = 0;
            const maxRetries = 10;

            while (retryCount < maxRetries) {
                securityData = await page.evaluate(() => {
                    const data: Record<string, string> = {};
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key) {
                            data[key] = localStorage.getItem(key) || "";
                        }
                    }
                    return data;
                });

                // 检查是否有数据
                if (
                    securityData["security-sdk/s_sdk_pri_key"] &&
                    securityData["security-sdk/s_sdk_pub_key"] &&
                    securityData["security-sdk/s_sdk_sign_data_key/web_protect"] &&
                    securityData["security-sdk/s_sdk_server_cert_key"] &&
                    securityData["security-sdk/s_sdk_crypt_sdk"]
                ) {
                    task.logger.info("Security SDK 数据已获取");
                    break;
                }

                retryCount++;
                task.logger.info(`等待 Security SDK 数据... (${retryCount}/${maxRetries})`);
                await page.waitForTimeout(1000);
            }

            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    extraParam: securityData,
                    userInfo: userInfo,
                },
            });
            return {
                code: 0,
                message: "成功",
                data: {
                    cookie: JSON.stringify(await task.steelBrowserContext?.cookies()),
                    extraParam: securityData,
                    userInfo,
                },
            };
        }
        default:
            // 处理 RUNNING 和其他可能的状态
            return { code: 500, message: "任务执行中断", data: {} };
    }
};
